import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingDown, Wallet, Wrench, Users, Compass, Truck } from "lucide-react";
import Layout, { PRIMARY_CTA, BookingLink } from "../components/Layout";
import SEO from "../components/SEO";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = `${BACKEND}/api/hero/a_cinematic_ultra_realistic_wide_angle_scene_at_w.jpg?v=3`;

const LENS = [
  { icon: Wallet, title: "Financial Control", desc: "Cash visibility, profit by truck, and the operating rhythm that keeps money from disappearing." },
  { icon: TrendingDown, title: "Fleet Economics & Utilization", desc: "Which trucks, lanes, and customers are creating margin — and which are quietly destroying it." },
  { icon: Wrench, title: "Maintenance Systems", desc: "Preventive discipline, repair reserve, and downtime patterns that stop eating your quarter." },
  { icon: Users, title: "Drivers & People", desc: "Onboarding, expectations, weekly accountability, and the reasons drivers stay or leave." },
  { icon: Compass, title: "Owner Independence & Management", desc: "The company runs on a scorecard — not on the owner's head. You can step away for two days without losing control." },
];

export default function Home() {
  return (
    <Layout>
      <SEO title="Driver Reborn Advisory | Fleet Profit and Operations Clarity" description="Driver Reborn Advisory helps established trucking fleet owners uncover profit leaks, strengthen back-office systems, and gain greater control over their operations." />
      {/* HERO — single dominant CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.72), rgba(10,10,10,0.9)), url(${HERO_IMG})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}/>
        <div className="relative container-narrow section">
          <div className="max-w-3xl">
            <div className="inline-block px-3 py-1 border border-[#FF6A00] text-[#FF6A00] text-xs uppercase tracking-widest mb-6">
              Driver Reborn Advisory
            </div>
            <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl leading-none uppercase" data-testid="hero-title">
              Your Fleet Is Moving.<br/>
              <span className="text-[#FF6A00]">But Can You Trust The<br/>Business Behind It?</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#E6E6E6] leading-relaxed max-w-2xl" data-testid="hero-sub">
              Driver Reborn Advisory helps established fleet owners uncover profit leaks, strengthen operating control, and build a business that can scale without depending on the owner for everything.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-start gap-4">
              <BookingLink testid="hero-cta">
                {PRIMARY_CTA} <ArrowRight size={20}/>
              </BookingLink>
              <Link to="/fleet-crossroads-scorecard" className="btn-ghost" data-testid="hero-scorecard-cta">
                Take the Free Fleet Crossroads Scorecard
              </Link>
            </div>
            <p className="mt-3 text-xs text-[#A1A1AA] uppercase tracking-widest">
              Free scorecard: 5 minutes. See where money, control, and capacity may be leaking.
            </p>
          </div>
        </div>
      </section>

      {/* RECOGNITION */}
      <section className="section bg-[#111111]">
        <div className="container-narrow max-w-3xl">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Recognition</div>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-6" data-testid="recognition-title">
            Revenue can look healthy.<br/>
            <span className="text-[#FF6A00]">The business behind it may not be.</span>
          </h2>
          <p className="text-[#E6E6E6] leading-relaxed">
            The trucks are moving. Customers are paying. On paper, the fleet is fine. But cash, maintenance, drivers, dispatch, and owner time are all under quiet pressure — and no one can point to exactly where the leak lives.
          </p>
        </div>
      </section>

      {/* ROOT PROBLEM */}
      <section className="section">
        <div className="container-narrow max-w-3xl">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">The root problem</div>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-4" data-testid="root-title">
            The business grew faster than the structure.
          </h2>
          <p className="text-[#E6E6E6] leading-relaxed">
            Eventually, the owner becomes the operating system. Every cash decision, dispatch exception, driver problem, maintenance choice, and customer pressure comes back to one person. That's not a business. That's a bottleneck wearing a company's name.
          </p>
        </div>
      </section>

      {/* FIVE-AREA DIAGNOSTIC LENS */}
      <section className="section bg-[#111111]">
        <div className="container-narrow">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">The five-area lens</div>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none max-w-3xl mb-10">
            Driver Reborn Advisory looks at five things. Every time.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {LENS.map((l) => (
              <div key={l.title} className="border border-[#2B2B2B] bg-[#0A0A0A] p-6" data-testid={`lens-${l.title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'')}`}>
                <l.icon className="text-[#FF6A00] mb-4" size={30}/>
                <h3 className="font-bebas text-2xl uppercase mb-2">{l.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COST OF LEAK — example only */}
      <section className="section">
        <div className="container-narrow max-w-3xl">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Illustrative example</div>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-6" data-testid="cost-title">
            What a small, quiet leak costs.
          </h2>
          <div className="border-l-4 border-[#FF6A00] bg-[#111111] p-6 mb-4">
            <p className="text-[#E6E6E6] leading-relaxed">
              5 trucks × $400/week = <span className="font-bebas text-[#FF6A00] text-2xl">$104,000/year</span> that never shows up on any report — because it's split across dispatch inefficiency, deadhead, avoidable maintenance, and driver churn.
            </p>
          </div>
          <p className="text-xs text-[#A1A1AA] italic" data-testid="cost-disclaimer">
            This is an illustrative example only, not a promise, guarantee, or projection for any specific fleet.
          </p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="section bg-[#111111]">
        <div className="container-narrow max-w-3xl">
          <div className="text-[#FF6A00] text-xs uppercase tracking-widest mb-2">Founder</div>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-4" data-testid="founder-title">
            Built From Around The Trucks, Not From The Outside Looking In.
          </h2>
          <p className="text-[#E6E6E6] leading-relaxed mb-4">
            Driver Reborn Advisory was founded by Brandon Winston, a second-generation trucking professional with more than 20 years of hands-on industry experience.
          </p>
          <p className="text-[#E6E6E6] leading-relaxed mb-4">
            Brandon grew up learning the business from his father — more than 30 years as a driver and truck owner across cross-country, cement, flatbed, heavy-haul, and piggyback freight. His father also studied accounting and taught Brandon to think beyond driving: business finances, taxes, operating decisions, and the responsibility that comes with owning trucks.
          </p>
          <p className="text-[#E6E6E6] leading-relaxed mb-4">
            Brandon followed his father into trucking but intentionally chose local and regional lanes so he could remain present for his own family. Over time his experience expanded beyond the seat — into instruction, training, recruiting, retention, dispatch conversations, management, and operational problem-solving.
          </p>
          <p className="text-[#E6E6E6] leading-relaxed mb-6">
            He became fascinated by the business behind the trucks: the patterns separating companies that operate with financial clarity and control from those that keep their owners trapped in constant reaction. That pattern recognition became Driver Reborn Advisory.
          </p>
          <div className="border-l-4 border-[#FF6A00] pl-4 mb-8" data-testid="founder-belief">
            <p className="font-bebas text-2xl uppercase tracking-wider text-[#FF6A00]">
              No pressure. No games. Just clarity.
            </p>
          </div>
          <Link to="/about" className="btn-ghost" data-testid="about-link">Read more about Brandon <ArrowRight size={16}/></Link>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section bg-[#111111]">
        <div className="container-tight text-center">
          <Truck className="text-[#FF6A00] mx-auto mb-4" size={40}/>
          <h2 className="font-bebas text-4xl sm:text-5xl uppercase leading-none mb-4">
            Start where every owner should.
          </h2>
          <p className="text-[#A1A1AA] mb-8">Talk through what is happening behind the trucks with Brandon — or start with the free Fleet Crossroads Scorecard.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <BookingLink className="btn-primary justify-center" testid="home-final-cta">
              {PRIMARY_CTA} <ArrowRight size={20}/>
            </BookingLink>
            <Link to="/fleet-crossroads-scorecard" className="btn-ghost justify-center" data-testid="home-final-scorecard-cta">
              Take the Free Scorecard
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
